<script setup>
import { ref, computed, nextTick, useTemplateRef, watch } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const videoPlayer = useTemplateRef('videoPlayer');
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

const appendTimestamp = url => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set('t', Date.now());
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const videoSrc = computed(() => {
  if (!attachment.value?.dataUrl) return '';

  return appendTimestamp(attachment.value.dataUrl);
});

const resetVideo = async () => {
  hasError.value = false;
  await nextTick();
  videoPlayer.value?.load();
};

watch(videoSrc, resetVideo);
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <div class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        ref="videoPlayer"
        :key="videoSrc"
        controls
        class="rounded-lg skip-context-menu"
        :src="videoSrc"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
